/**
 * Talk-down transport state (ENG-2135): takes a recorded Blob, uploads it,
 * then polls for the camera's outcome. The recorder is separate on purpose
 * (see useAudioRecorder); a live push-to-talk swaps this hook for a relay
 * publish and keeps the same control.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  describeTalkDownErrorDetail,
  describeTalkDownOutcome,
  describeTalkDownOutcomeDetail,
  describeTalkDownSendError,
  pollTalkDownOutcome,
  sendTalkDown,
  TALK_DOWN_MAX_BYTES,
  TALK_DOWN_NOT_RECEIVED_MESSAGE,
  TALK_DOWN_SENT_MESSAGE,
  TALK_DOWN_TOO_LARGE_MESSAGE,
  TalkDownPollOptions,
} from "./talkDownApi";
import { noteTalkDownErrorCode } from "./talkDownCapabilityStore";

export type TalkDownPhase =
  | "idle"
  | "sending"
  | "waiting"
  | "played"
  | "failed"
  | "timeout"
  | "error";

export interface TalkDownState {
  phase: TalkDownPhase;
  message: string;
  /** Technical cause for the staff-only detail line. */
  detail?: string;
}

export const IDLE_TALK_DOWN: TalkDownState = { phase: "idle", message: "" };

export interface UseTalkDownOptions {
  cameraId: string | number | null | undefined;
  cameraName: string;
  poll?: TalkDownPollOptions;
}

export interface TalkDown {
  state: TalkDownState;
  /** Upload is in flight or the outcome is still being polled. */
  busy: boolean;
  submit: (audio: Blob) => void;
  reset: () => void;
}

export const useTalkDown = ({
  cameraId,
  cameraName,
  poll,
}: UseTalkDownOptions): TalkDown => {
  const [state, setState] = useState<TalkDownState>(IDLE_TALK_DOWN);
  const abortRef = useRef<AbortController | null>(null);
  const pollRef = useRef(poll);
  pollRef.current = poll;

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  useEffect(() => cancel, [cancel]);

  // A new camera means any in-flight outcome belongs to the old one.
  useEffect(() => {
    cancel();
    setState(IDLE_TALK_DOWN);
  }, [cameraId, cancel]);

  const submit = useCallback(
    (audio: Blob) => {
      if (cameraId == null || cameraId === "") return;
      if (audio.size > TALK_DOWN_MAX_BYTES) {
        setState({ phase: "error", message: TALK_DOWN_TOO_LARGE_MESSAGE });
        return;
      }
      cancel();
      const controller = new AbortController();
      abortRef.current = controller;
      const live = () =>
        abortRef.current === controller && !controller.signal.aborted;

      setState({ phase: "sending", message: "Sending..." });
      sendTalkDown(cameraId, audio).then(
        async (dispatch) => {
          if (!live()) return;
          setState({ phase: "waiting", message: TALK_DOWN_SENT_MESSAGE });
          const result = await pollTalkDownOutcome(
            dispatch.site_id,
            dispatch.command_id,
            { ...pollRef.current, signal: controller.signal }
          );
          if (!live() || result.kind === "aborted") return;
          if (result.kind === "not_received") {
            setState({
              phase: "failed",
              message: TALK_DOWN_NOT_RECEIVED_MESSAGE,
              detail: `job command_id=${dispatch.command_id} · no status row`,
            });
            return;
          }
          const row = result.kind === "done" ? result.row : null;
          const outcome = describeTalkDownOutcome(row, cameraName);
          setState({
            phase: row == null ? "timeout" : outcome.ok ? "played" : "failed",
            message: outcome.message,
            detail: outcome.ok ? undefined : describeTalkDownOutcomeDetail(row),
          });
          // ENG-1721: a camera without a speaker hides its button for the
          // rest of the page session. Last: it can unmount this control.
          noteTalkDownErrorCode(cameraId, row?.result?.error_code);
        },
        (reason) => {
          if (!live()) return;
          setState({
            phase: "error",
            message: describeTalkDownSendError(reason),
            detail: describeTalkDownErrorDetail(reason),
          });
        }
      );
    },
    [cameraId, cameraName, cancel]
  );

  const reset = useCallback(() => {
    cancel();
    setState(IDLE_TALK_DOWN);
  }, [cancel]);

  return {
    state,
    busy: state.phase === "sending" || state.phase === "waiting",
    submit,
    reset,
  };
};
